import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { SprintPacketSurveyStatus } from '@marketing-milk/interfaces'
import { useRefreshSurveyLink } from 'hooks/api/useSprintPackets'
import { radius, SurfacePalette, type } from '../../tokens'
import { HiddenFromClientChip } from '../../components/HiddenFromClientChip'
import { SectionCard } from './SectionCard'

interface Props {
  palette: SurfacePalette
  businessID: number
  packetID: number
  survey: SprintPacketSurveyStatus
  /**
   * Whether the business has a Salesforce account connected (`sfAccountId`).
   * Passed in rather than derived here: the packet payload carries the link, not
   * the reason it's missing, and the editor already holds the business.
   */
  businessConnected: boolean
}

const BUTTON = (palette: SurfacePalette): React.CSSProperties => ({
  padding: '6px 14px',
  borderRadius: radius.sm,
  border: `1px solid ${palette.hairline}`,
  background: palette.surface,
  color: palette.text,
  fontFamily: type.family.mono,
  fontSize: type.size.eyebrow,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
})

const adminBusinessEditorURL = (businessID: number) =>
  `/admin/business/beta/accounts/edit/${businessID}`

/**
 * Edit-mode home of the client survey. The survey itself lives in Salesforce
 * (slice 11) — this card shows the link snapshotted onto the packet at create,
 * and the button that re-snapshots it when the sprint has rolled over.
 *
 * With no link the card stays (dimmed, un-numbered) so the warning and its fix
 * have somewhere to live; Present hides the slide entirely.
 */
export function ClientSurveySection({
  palette,
  businessID,
  packetID,
  survey,
  businessConnected,
}: Props) {
  const [copied, setCopied] = useState(false)
  const refresh = useRefreshSurveyLink(packetID)
  const { link } = survey

  const copyLink = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be unavailable (permissions, http) — the QR is still
      // scannable, so a failed copy still leaves a manual path.
    }
  }

  const refreshButton = (
    <button
      type="button"
      onClick={() => refresh.mutate()}
      disabled={refresh.isLoading}
      style={{ ...BUTTON(palette), opacity: refresh.isLoading ? 0.6 : 1 }}
    >
      {refresh.isLoading ? 'Refreshing…' : 'Refresh survey link'}
    </button>
  )

  // A failed refresh deliberately leaves the stored link (and the QR above it)
  // exactly as it was — the error says so rather than the card going empty.
  const refreshError = refresh.isError ? (
    <div
      style={{
        fontFamily: type.family.sans,
        fontSize: type.size.eyebrow,
        color: '#d64545',
        maxWidth: 320,
      }}
    >
      Couldn't reach Salesforce. The saved link is unchanged — try again in a moment.
    </div>
  ) : null

  return (
    <SectionCard
      palette={palette}
      sectionKey="client-survey"
      title="Client Survey"
      muted={!link}
      showNumber={!!link}
      // With no link the slide is already out of the deck and this card is
      // carrying the fix. An eye toggle here would silently lose to that derived
      // rule — a control that lies about what it does. Any stored hide on this
      // key is left untouched and takes effect again once a link exists.
      hideControl={link ? undefined : 'unavailable'}
      commentAnchor={{ type: 'section', id: `${packetID}:client-survey`, label: 'Client Survey' }}
    >
      {link ? (
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div
            style={{
              padding: 12,
              borderRadius: radius.md,
              border: `1px solid ${palette.hairline}`,
              background: '#fff',
            }}
          >
            <QRCodeSVG value={link} size={144} />
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}
          >
            <p
              style={{
                margin: 0,
                maxWidth: 360,
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                color: palette.textMuted,
              }}
            >
              The client scans this to open their sprint survey. Responses go to Salesforce, so
              nothing shows up here.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={copyLink} style={BUTTON(palette)}>
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              {/* Always offered, not just on the empty state: the expected
                  failure is a stale link (the sprint rolled over after the
                  packet was made), not a missing one. */}
              {refreshButton}
            </div>
            {refreshError}
          </div>
        </div>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}
        >
          <HiddenFromClientChip
            palette={palette}
            title="There's no survey link on this packet, so the slide is skipped in Present."
          />
          {businessConnected ? (
            <>
              {/* Deliberately not "Salesforce has no link for this client": we
                  can't tell an empty field from a create-time outage, and the
                  field is populated in practice, so don't assert a cause the
                  data doesn't support. */}
              <p
                style={{
                  margin: 0,
                  maxWidth: 420,
                  fontFamily: type.family.sans,
                  fontSize: type.size.body,
                  color: palette.textMuted,
                }}
              >
                No survey link is saved for this packet. Salesforce is connected — refresh to try
                again.
              </p>
              {refreshButton}
              {refreshError}
            </>
          ) : (
            // Refresh cannot fix a missing connection, so it isn't offered here.
            // The link stops at the business editor on purpose: the nested
            // wizards always initialize at step zero, so a deep link to the
            // Integrations tab lands on the wrong step.
            <p
              style={{
                margin: 0,
                maxWidth: 460,
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                color: palette.textMuted,
              }}
            >
              This business isn't connected to Salesforce, so there's no survey to link to. Open the{' '}
              <a
                href={adminBusinessEditorURL(businessID)}
                style={{ color: palette.accent, textDecoration: 'underline' }}
              >
                business editor in the admin panel
              </a>{' '}
              and set the Salesforce account under <strong>Settings → Integrations</strong>, then
              come back and refresh this page.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  )
}
